/**
 * Plan de tratamiento, presupuesto, abonos y saldo.
 * Todo el dinero se maneja en CENTAVOS (enteros) para no perder centavos por redondeos de coma flotante.
 */
import { codigoTratamiento, faseTratamiento, puentesDe, materialCorona, type Hallazgo } from "./odontograma.ts";
import { cuadrantesDeNota } from "./indicadores.ts";

export const ARANCEL_BASE: readonly (readonly [codigo: string, nombre: string, dolares: number])[] = [
  ["CON", "Consulta y diagnóstico", 25], ["RX", "Radiografía periapical", 10],
  ["PRO", "Profilaxis dental profunda (destartraje y pulido)", 35], ["PRS", "Profilaxis dental simple (limpieza y pulido)", 25], ["IHO", "Instrucción de higiene oral y control de placa", 10],
  ["RAS", "Raspado y alisado radicular (por cuadrante)", 60], ["TIN", "Desbridamiento de gingivitis o periodontitis necrosante", 50], ["ABP", "Drenaje y tratamiento de absceso periodontal", 50],
  ["GNG", "Gingivectomía o gingivoplastia (por cuadrante)", 80], ["CPC", "Cirugía periodontal a colgajo (por cuadrante)", 150], ["INJ", "Injerto gingival o recubrimiento radicular (por pieza)", 200],
  ["MPE", "Mantenimiento periodontal (control y limpieza)", 40], ["FLU", "Aplicación de barniz de flúor", 15], ["SEL", "Sellante de fosas y fisuras", 20],
  ["RES1", "Resina, 1 superficie", 35], ["RES2", "Resina, 2 superficies", 45], ["RES3", "Resina, 3 o más superficies", 55], ["ION", "Ionómero de vidrio", 25], ["AMA", "Amalgama", 30],
  ["INC", "Incrustación", 220], ["CV", "Carilla de porcelana", 250],
  ["END1", "Endodoncia unirradicular", 150], ["END2", "Endodoncia birradicular", 200], ["END3", "Endodoncia multirradicular", 250],
  ["PPT", "Pulpotomía", 45], ["PPC", "Pulpectomía", 60], ["CA", "Corona de acero", 55], ["ME", "Mantenedor de espacio", 80],
  ["EXO", "Exodoncia simple", 35], ["EXQ", "Exodoncia quirúrgica o de tercer molar", 90],
  ["QUI", "Quistectomía o enucleación con biopsia", 150], ["APX", "Apicectomía con obturación retrógrada", 180], ["VEN", "Ventana quirúrgica y botón para tracción ortodóncica", 120],
  ["FRE", "Frenectomía o frenotomía", 70], ["ALV", "Alveoloplastia o remoción de torus", 90],
  ["PER", "Perno muñón", 90], ["COR", "Corona metal-porcelana", 280], ["CZR", "Corona de zirconio", 380], ["CCE", "Corona de cerámica", 350], ["CDL", "Corona de disilicato de litio", 420],
  ["PF", "Prótesis fija (por unidad)", 280], ["PR", "Prótesis removible", 350], ["PT", "Prótesis total (por arcada)", 450], ["IMP", "Implante dental", 900], ["BLA", "Blanqueamiento dental", 180], ["PLO", "Placa oclusal miorrelajante (ATM)", 120],
];

export const FASES = ["Diagnóstico y urgencia", "Higiénica y preventiva", "Restauradora", "Rehabilitación", "Mantenimiento y estética"] as const;
export const METODOS_PAGO = ["Efectivo", "Transferencia", "Tarjeta de débito", "Tarjeta de crédito", "Cheque", "DeUna"] as const;
export type MetodoPago = (typeof METODOS_PAGO)[number];

export const aCentavos = (dolares: number) => Math.round(dolares * 100);
export const dinero = (centavos: number) =>
  (centavos < 0 ? "-$" : "$") + (Math.abs(centavos) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Convierte un texto como "45", "45.5" o "45,50" a centavos. Devuelve null si no es un monto válido. */
export function leerMonto(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) && v >= 0 && v < 1e7 ? aCentavos(v) : null;
  if (typeof v !== "string") return null;
  const t = v.trim().replace(/^\$/, "").replace(",", ".");
  if (!/^\d{1,7}(\.\d{1,2})?$/.test(t)) return null;
  return aCentavos(Number(t));
}

/* ---------- sugerencias a partir del odontograma ---------- */

export interface ItemSugerido { clave: string; hallazgoId: string | null; codigo: string; diente: number | null; superficies: string; cantidad: number; fase: number; /** el material ya está elegido: no se reemplaza por otro tratamiento de la clínica */ fijo?: boolean }

/** Procedimientos que el odontograma puede sugerir por sí solo; cada clínica puede elegir otro tratamiento en su lugar (ver `reemplazos`). */
export const CODIGOS_SUGERIBLES: readonly string[] = ["CON", "PRO", "IHO", "RAS", "TIN", "ABP", "GNG", "MPE", "FLU", "SEL", "RES1", "RES2", "RES3", "ION", "AMA", "INC", "CV", "END1", "END2", "END3",
  "PPT", "PPC", "CA", "ME", "EXO", "EXQ", "QUI", "APX", "VEN", "FRE", "ALV", "PER", "COR", "PF", "PR", "PT", "IMP"];

/**
 * Procedimientos que el odontograma pide: uno por hallazgo "por realizar" con tratamiento, más la consulta y,
 * según los diagnósticos generales, profilaxis o raspado. La clave evita duplicados al volver a sincronizar.
 * `reemplazos` (código sugerido → código que la clínica prefiere) cambia el procedimiento, pero no la clave ni la fase del plan.
 */
export function itemsSugeridos(hallazgos: (Hallazgo & { id: string })[], reemplazos: Readonly<Record<string, string>> = {}): ItemSugerido[] {
  const out: ItemSugerido[] = [{ clave: "g:CON", hallazgoId: null, codigo: "CON", diente: null, superficies: "", cantidad: 1, fase: 0 }];
  // la prótesis removible se cobra una vez por aparato (en su primer pilar), no por cada pieza del tramo
  const otrasPiezasRemovible = new Set(puentesDe(hallazgos, "removible").flatMap((g) => g.hallazgos.slice(1).map((h) => h.id)));
  for (const h of hallazgos) {
    if (h.estado !== "por_realizar" || otrasPiezasRemovible.has(h.id)) continue;
    const codigo = codigoTratamiento(h);
    if (!codigo) continue;
    const fase = faseTratamiento(codigo, h.diente, hallazgos);
    if (h.diente === null) out.push({ clave: `g:${codigo}`, hallazgoId: null, codigo, diente: null, superficies: "", cantidad: 1, fase });
    else out.push({ clave: `h:${h.id}`, hallazgoId: h.id, codigo, diente: h.diente, superficies: h.superficies.join(""), cantidad: 1, fase, ...(h.tipo === "corona" && materialCorona(h.nota).explicito ? { fijo: true } : {}) });
  }
  const cies = new Set(hallazgos.map((h) => h.cie10));
  const general = (clave: string, codigo: string, cantidad: number, fase: number) => out.push({ clave, hallazgoId: null, codigo, diente: null, superficies: "", cantidad, fase });
  // placa o cálculo, gingivitis o agrandamiento de la encía: profilaxis dental profunda y enseñanza de higiene
  if (cies.has("K05.0") || cies.has("K05.1") || cies.has("K03.6") || cies.has("K06.1")) { general("g:PRO", "PRO", 1, 1); general("g:IHO", "IHO", 1, 1); }
  // periodontitis: raspado y alisado radicular por cuadrante (los que se marcaron; si no se dice, los cuatro) y mantenimiento
  const periodontitis = ["K05.2", "K05.3", "K05.4", "K05.5", "K05.6"];
  if (periodontitis.some((c) => cies.has(c))) {
    const dx = hallazgos.find((h) => h.diente === null && h.cie10 !== null && periodontitis.includes(h.cie10));
    general("g:RAS", "RAS", (dx && cuadrantesDeNota(dx.nota)) || 4, 1);
    general("g:MPE", "MPE", 1, 4);
  }
  if (cies.has("A69.1")) { general("g:TIN", "TIN", 1, 0); general("g:IHO", "IHO", 1, 1); }
  if (hallazgos.some((h) => h.cie10 === "K05.2" && /absceso/i.test(h.nota))) general("g:ABP", "ABP", 1, 0);
  if (cies.has("K06.1")) general("g:GNG", "GNG", 1, 2);
  const vistas = new Set<string>();
  return out.filter((i) => (vistas.has(i.clave) ? false : (vistas.add(i.clave), true)))
    .map((i) => (!i.fijo && Object.hasOwn(reemplazos, i.codigo) ? { ...i, codigo: reemplazos[i.codigo] } : i));
}

/**
 * Cómo queda el hallazgo del odontograma cuando se realiza su tratamiento:
 * la caries pasa a obturación realizada (resina, ionómero o amalgama), la extracción a pieza ausente y el resto a "realizado".
 */
export function hallazgoTrasRealizar(h: Hallazgo, codigo: string): Hallazgo {
  if (h.tipo === "caries") {
    const material = codigo === "ION" ? "Ionómero de vidrio" : codigo === "AMA" ? "Amalgama" : "Resina";
    return { ...h, tipo: "obturacion", estado: "realizado", cie10: null, nota: material };
  }
  // el diente incluido que se extrajo ya no está en la boca; lo demás (supernumerario, quiste, tracción…) queda como «realizado»
  if (h.tipo === "extraccion" || h.tipo === "incluido") return { ...h, tipo: "ausente", estado: "realizado", superficies: [], cie10: "K08.1", nota: "" };
  return { ...h, estado: "realizado" };
}

/* ---------- procedimientos agregados a mano ---------- */

/** Procedimientos que se hacen sobre ciertas caras del diente: resinas, amalgama, ionómero e incrustación. */
export const CODIGOS_CON_CARAS: readonly string[] = ["RES1", "RES2", "RES3", "AMA", "ION", "INC"];
/** De esos, los que no se pueden marcar en el odontograma sin decir las caras. */
export const CARAS_OBLIGATORIAS: readonly string[] = ["RES1", "RES2", "RES3", "AMA", "ION"];
export const conCaras = (codigo: string) => CODIGOS_CON_CARAS.includes(codigo);
/** La resina del arancel según cuántas caras se tratan (1, 2 o 3 o más). */
export const codigoResina = (caras: number) => (caras >= 3 ? "RES3" : caras === 2 ? "RES2" : "RES1");

/**
 * Caras que se ofrecen en una pieza: O (oclusal o incisal), V, M, D y P (palatino) en las superiores o L (lingual) en las inferiores.
 * En Donna el palatino y el lingual se guardan con la misma letra «L».
 */
export function carasDePieza(diente: number | null): { valor: string; etiqueta: string }[] {
  const superior = diente !== null && [1, 2, 5, 6].includes(Math.floor(diente / 10));
  return [{ valor: "O", etiqueta: "O · Oclusal / incisal" }, { valor: "V", etiqueta: "V · Vestibular" }, { valor: "M", etiqueta: "M · Mesial" }, { valor: "D", etiqueta: "D · Distal" },
    superior ? { valor: "L", etiqueta: "P · Palatino" } : { valor: "L", etiqueta: "L · Lingual" }];
}

/**
 * Hallazgo «por realizar» que el odontograma debe mostrar para un procedimiento que se agregó a mano al plan
 * (null si el procedimiento no se dibuja en una sola pieza, como el puente, la prótesis o el blanqueamiento).
 */
export function hallazgoDeProcedimiento(codigo: string, diente: number | null, superficies: string): Hallazgo | null {
  if (diente === null) return null;
  const caras = [...superficies] as Hallazgo["superficies"];
  const base = { diente, estado: "por_realizar" as const, cie10: null, superficies: [] as Hallazgo["superficies"], nota: "" };
  if (/^RES[123]$/.test(codigo)) return { ...base, tipo: "obturacion", superficies: caras, nota: "Resina" };
  switch (codigo) {
    case "AMA": return { ...base, tipo: "obturacion", superficies: caras, nota: "Amalgama" };
    case "ION": return { ...base, tipo: "obturacion", superficies: caras, nota: "Ionómero de vidrio" };
    case "INC": return { ...base, tipo: "incrustacion", superficies: caras };
    case "CV": return { ...base, tipo: "carilla" };
    case "SEL": return { ...base, tipo: "sellante" };
    case "END1": case "END2": case "END3": return { ...base, tipo: "endodoncia" };
    case "PPT": return { ...base, tipo: "pulpotomia" };
    case "PPC": return { ...base, tipo: "pulpectomia" };
    case "CA": return { ...base, tipo: "coronaacero" };
    case "ME": return { ...base, tipo: "mantenedor" };
    case "EXO": case "EXQ": return { ...base, tipo: "extraccion" };
    case "COR": return { ...base, tipo: "corona", nota: "Metal-porcelana" };
    case "CZR": return { ...base, tipo: "corona", nota: "Zirconio" };
    case "CCE": return { ...base, tipo: "corona", nota: "Cerámica" };
    case "CDL": return { ...base, tipo: "corona", nota: "Disilicato de litio" };
    case "PER": return { ...base, tipo: "perno" };
    case "IMP": return { ...base, tipo: "implante" };
    default: return null;
  }
}

/* ---------- totales ---------- */

/** `descClub`: descuento del club de beneficios que le toca a un procedimiento pendiente (se compara con el descuento del plan y manda el mayor). */
export interface ItemPlan { precio: number; cantidad: number; estado: "pendiente" | "realizado"; descAplicado: number | null; descClub?: number | null }
export interface Totales {
  subtotal: number; descuento: number; base: number; iva: number; total: number;
  realizado: number; pagado: number; saldo: number; porCobrarHoy: number; items: number; hechos: number;
}

/** Valor de un ítem con descuento. El descuento de un ítem realizado queda fijo al que había cuando se hizo. */
export function valorItem(i: ItemPlan, descuentoPct: number): number {
  const d = i.estado === "realizado" && i.descAplicado !== null ? i.descAplicado : Math.max(descuentoPct, i.descClub ?? 0);
  return Math.round(i.precio * i.cantidad * (1 - d / 100));
}

export function totalesPlan(items: ItemPlan[], descuentoPct: number, ivaPct: number, abonos: number[]): Totales {
  const subtotal = items.reduce((s, i) => s + i.precio * i.cantidad, 0);
  const base = items.reduce((s, i) => s + valorItem(i, descuentoPct), 0);
  const iva = Math.round(base * ivaPct / 100);
  const baseHecha = items.filter((i) => i.estado === "realizado").reduce((s, i) => s + valorItem(i, descuentoPct), 0);
  const realizado = baseHecha + Math.round(baseHecha * ivaPct / 100);
  const pagado = abonos.reduce((s, a) => s + a, 0);
  const total = base + iva;
  return {
    subtotal, descuento: subtotal - base, base, iva, total, realizado, pagado,
    saldo: total - pagado, porCobrarHoy: realizado - pagado, items: items.length, hechos: items.filter((i) => i.estado === "realizado").length,
  };
}

/** Valor de un procedimiento con su IVA (lo que realmente se le cobra al paciente). */
export const valorConIva = (i: ItemPlan, descuentoPct: number, ivaPct: number) => { const v = valorItem(i, descuentoPct); return v + Math.round(v * ivaPct / 100); };

export interface ItemCuenta { id: string; valor: number; realizado: boolean; fecha: string | null; pagadoAntes: number; /** abonos registrados directamente sobre este tratamiento */ vinculado?: number }
export interface PagoItem { abono: number; saldo: number }

/**
 * Cuánto lleva pagado cada procedimiento y cuánto le falta. Primero se descuenta lo pagado antes de Donna y los abonos hechos directamente sobre
 * ese tratamiento; lo que sobre de un tratamiento y los abonos generales del paciente cubren los procedimientos realizados en orden, del más
 * antiguo al más nuevo (como una cuenta corriente). Un procedimiento aún no realizado muestra lo que se le abonó y lo que le falta de su valor.
 * `aFavor` es lo abonado de más.
 */
export function repartirPagos(items: readonly ItemCuenta[], abonosGenerales: number): { porItem: Map<string, PagoItem>; aFavor: number } {
  const porItem = new Map<string, PagoItem>();
  let resto = Math.max(0, abonosGenerales);
  const propio = new Map<string, number>();
  for (const i of items) {
    const antes = Math.min(Math.max(0, i.pagadoAntes), i.valor);
    const vinculado = Math.max(0, i.vinculado ?? 0);
    const suyo = Math.min(Math.max(0, i.valor - antes), vinculado);
    resto += vinculado - suyo;       // si se abonó de más sobre un tratamiento, el exceso pasa al pozo general
    propio.set(i.id, antes + suyo);
  }
  const orden = items.filter((i) => i.realizado).sort((a, b) => (a.fecha ?? "").localeCompare(b.fecha ?? "") || a.id.localeCompare(b.id));
  for (const i of orden) {
    const base = propio.get(i.id)!;
    const asignado = Math.min(Math.max(0, i.valor - base), resto);
    resto -= asignado;
    porItem.set(i.id, { abono: base + asignado, saldo: i.valor - base - asignado });
  }
  for (const i of items) if (!i.realizado) { const base = propio.get(i.id)!; porItem.set(i.id, { abono: base, saldo: i.valor - base }); }
  return { porItem, aFavor: resto };
}

/** Plan de pagos: entrada y cuotas iguales; la última cuota absorbe los centavos del redondeo. */
export function cuotas(saldo: number, entrada: number, n: number): number[] {
  const deuda = Math.max(saldo - Math.max(Math.min(entrada, saldo), 0), 0);
  const k = Math.max(1, Math.min(Math.floor(n), 36));
  const c = Math.floor(deuda / k);
  return Array.from({ length: k }, (_, i) => (i === k - 1 ? deuda - c * (k - 1) : c));
}
